import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Newspaper } from "lucide-react";
import { motion } from "framer-motion";
import { ArticleActions } from "@/components/site/ArticleActions";
import { NewsletterSignup } from "@/components/site/NewsletterSignup";
import { API } from "@/lib/api";

export default function Insights() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/posts`).then((res) => setPosts(res.data.posts)).catch(() => setPosts([]));
  }, []);

  return (
    <div data-testid="insights-page" className="min-h-screen bg-night text-foreground">
      <header className="border-b border-white/10 bg-night/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="insights-brand" className="flex items-center">
            <span className="bg-white rounded-md px-3 py-1.5 flex items-center shadow-md shadow-black/30">
              <img src="/globus-lockup.png" alt="Globus Financial Services" className="h-7 w-auto object-contain" />
            </span>
          </Link>
          <Link data-testid="insights-back-link" to="/" className="text-sm text-[#A0AAB2] hover:text-gold transition-colors flex items-center gap-2">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-20">
        <p className="overline-label mb-4">Insights</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">Rates, policy & borrowing guides.</h1>
        <p className="text-[#A0AAB2] text-base md:text-lg leading-relaxed mb-4">
          Notes from the advisor's desk — rate movements, lender policy changes and practical guidance for borrowers.
        </p>
        <p className="text-xs text-gold/80 tracking-wide mb-8">
          A fresh RBI &amp; economy snapshot is published here automatically every day.
        </p>

        <div className="bg-slate2 border border-white/10 p-6 mb-14" data-testid="insights-newsletter-box">
          <p className="font-serif text-xl mb-1">Get the weekly digest</p>
          <p className="text-sm text-muted-foreground mb-4">Every Monday morning — the week's RBI and lending moves in your inbox.</p>
          <NewsletterSignup testId="insights-newsletter" />
        </div>

        {posts === null ? (
          <div className="flex justify-center py-16" data-testid="insights-loading"><Loader2 className="animate-spin text-gold" size={28} /></div>
        ) : posts.length === 0 ? (
          <div className="bg-slate2 border border-white/10 py-20 text-center" data-testid="insights-empty">
            <Newspaper size={28} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-sm">Insights are on their way — check back soon.</p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="insights-list">
            {posts.map((post, i) => (
              <motion.article
                key={post.id}
                id={`post-${post.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                data-testid={`insight-${post.id}`}
                className="bg-slate2 border border-white/10 p-8"
              >
                {post.image_url && (
                  <img
                    src={`${API}/images/${post.image_url}`}
                    alt={post.title}
                    data-testid={`insight-image-${post.id}`}
                    className="w-full aspect-video object-cover rounded-md border border-white/10 mb-6"
                  />
                )}
                <div className="flex items-center gap-4 text-xs mb-4">
                  <span className="text-gold uppercase tracking-[0.2em]">{post.tag}</span>
                  <span className="text-muted-foreground">
                    {new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                  </span>
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl mb-4">{post.title}</h2>
                <p className="text-[#A0AAB2] leading-relaxed text-sm md:text-base whitespace-pre-line">{post.body}</p>
                <ArticleActions post={post} />
              </motion.article>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Globus Financial Services · Sahibabad, Uttar Pradesh 201005, India · GSTIN: 09AEFPK4400F1ZW
        </div>
      </footer>
    </div>
  );
}
